import React, { useEffect, useCallback } from 'react';
import { Button, Modal, Input, InputNumber, DatePicker, Table, Tabs, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useC208Mode } from './hooks/useC208Mode';
import { useC208Record } from './hooks/useC208Record';
import { useC208Navigation } from './hooks/useC208Navigation';
import { useModulePermission } from '../../hooks/usePermission';
import LookupField from '../../components/LookupField';
import type { FC151 } from '../../services/c208Api';
import './C208Form.css';

const { TextArea } = Input;
const { TabPane } = Tabs;

const C208Form: React.FC = () => {
  const { mode, setMode, isEditing } = useC208Mode();
  const permissions = useModulePermission('pwc208f1');

  const {
    record,
    formData,
    details,
    loading,
    loadRecord,
    getField,
    setField,
    resetForAdd,
    revertToRecord,
    handleSave,
    handleDelete,
    addDetailRow,
    deleteDetailRow,
    updateDetailField,
  } = useC208Record(setMode);

  const loadRecordWithNav = useCallback(
    async (id: string) => { await loadRecord(id); },
    [loadRecord]
  );

  const { allIds, currentIdx, setCurrentIdx, loadList, navFirst, navPrev, navNext, navLast } =
    useC208Navigation(loadRecordWithNav);

  useEffect(() => {
    loadList().then((ids) => {
      if (ids.length > 0) { setCurrentIdx(0); loadRecord(ids[0]); }
    });
  }, [loadList, loadRecord, setCurrentIdx]);

  const handleAddNew = () => { resetForAdd(); };
  const handleEdit = () => {
    if (!record) { message.warning('請先選取一筆資料'); return; }
    setMode('edit');
  };
  const handleRevert = () => { revertToRecord(); };
  const handleSaveClick = async () => {
    const ok = await handleSave(mode);
    if (ok) { await loadList(); }
  };
  const handleDeleteClick = () => {
    if (!record) return;
    Modal.confirm({
      title: '確認刪除',
      content: `確認刪除來樣分析單 ${record.fc150_01}？`,
      okText: '刪除',
      cancelText: '取消',
      okType: 'danger',
      onOk: async () => {
        const ok = await handleDelete(record.fc150_01);
        if (ok) {
          const newIds = await loadList();
          if (newIds.length > 0) { setCurrentIdx(0); loadRecord(newIds[0]); }
        }
      },
    });
  };

  // Detail table columns
  const detailColumns: ColumnsType<FC151> = [
    { title: '項次', dataIndex: 'fc151_02', width: 50 },
    {
      title: '測試代號', dataIndex: 'fc151_13', width: 90,
      render: (v: string, _: FC151, idx: number) =>
        isEditing ? (
          <Input value={v} onChange={(e) => updateDetailField(idx, 'fc151_13', e.target.value)} size="small" />
        ) : v,
    },
    {
      title: '檢驗規範', dataIndex: 'fc151_11', width: 100,
      render: (v: string, _: FC151, idx: number) =>
        isEditing ? (
          <Input value={v} onChange={(e) => updateDetailField(idx, 'fc151_11', e.target.value)} size="small" />
        ) : v,
    },
    {
      title: '允收標準', dataIndex: 'fc151_12', width: 120,
      render: (v: string, _: FC151, idx: number) =>
        isEditing ? (
          <Input value={v} onChange={(e) => updateDetailField(idx, 'fc151_12', e.target.value)} size="small" />
        ) : v,
    },
    {
      title: '客戶檢驗規範', dataIndex: 'fc151_21', width: 110,
      render: (v: string, _: FC151, idx: number) =>
        isEditing ? (
          <Input value={v} onChange={(e) => updateDetailField(idx, 'fc151_21', e.target.value)} size="small" />
        ) : v,
    },
    {
      title: '客戶允收標準', dataIndex: 'fc151_22', width: 120,
      render: (v: string, _: FC151, idx: number) =>
        isEditing ? (
          <Input value={v} onChange={(e) => updateDetailField(idx, 'fc151_22', e.target.value)} size="small" />
        ) : v,
    },
    {
      title: '操作', width: 60,
      render: (_: unknown, __: FC151, idx: number) =>
        isEditing ? (
          <Button size="small" danger onClick={() => deleteDetailRow(idx)}>刪除</Button>
        ) : null,
    },
  ];

  const gf = <K extends keyof typeof formData>(k: K) => formData[k];

  return (
    <div className="c208-form">
      <Tabs defaultActiveKey="1">
        {/* Tab 1: 基本資料 */}
        <TabPane tab="基本資料" key="1">
          <div className="form-section">
            <div className="form-grid">
              <span className="form-label">來樣單號</span>
              <Input value={gf('fc150_01') as string} disabled />
              <span className="form-label">來樣日期</span>
              <DatePicker
                value={gf('fc150_11') ? dayjs(gf('fc150_11') as string) : null}
                onChange={(d) => setField('fc150_11', d ? d.toISOString() : null)}
                disabled={!isEditing}
                style={{ width: '100%' }}
              />
              <span className="form-label">客戶代號</span>
              <LookupField
                lookupType="customer"
                value={gf('fc150_12') as string}
                onChange={(v) => setField('fc150_12', v)}
                onCodeConfirm={(v) => setField('fc150_12', v)}
                disabled={!isEditing}
                maxLength={12}
              />
              <span className="form-label">部門代號</span>
              <LookupField
                lookupType="department"
                value={gf('fc150_13') as string}
                onChange={(v) => setField('fc150_13', v)}
                onCodeConfirm={(v) => setField('fc150_13', v)}
                disabled={!isEditing}
                maxLength={4}
              />
              <span className="form-label">布樣用途</span>
              <LookupField lookupType="sampleUsage" value={(gf('fc150_22') as string) || ''}
                onChange={(code) => setField('fc150_22', code)}
                disabled={!isEditing} title="布樣用途搜尋" />
              <span className="form-label">樣布需要量</span>
              <div style={{ display: 'flex', gap: 4 }}>
                <InputNumber
                  value={gf('fc150_261') as number}
                  onChange={(v) => setField('fc150_261', v ?? 0)}
                  disabled={!isEditing}
                  style={{ flex: 1 }}
                />
                <Input
                  value={gf('fc150_262') as string}
                  onChange={(e) => setField('fc150_262', e.target.value)}
                  disabled={!isEditing}
                  maxLength={3}
                  style={{ width: 60 }}
                  placeholder="單位"
                />
              </div>
              <span className="form-label">目標價格</span>
              <div style={{ display: 'flex', gap: 4 }}>
                <LookupField
                  lookupType="currency"
                  value={gf('fc150_241') as string}
                  onChange={(v) => setField('fc150_241', v)}
                  onCodeConfirm={(v) => setField('fc150_241', v)}
                  disabled={!isEditing}
                  maxLength={3}
                  style={{ width: 60 }}
                />
                <InputNumber
                  value={gf('fc150_242') as number}
                  onChange={(v) => setField('fc150_242', v ?? 0)}
                  disabled={!isEditing}
                  style={{ flex: 1 }}
                />
                <Input
                  value={gf('fc150_243') as string}
                  onChange={(e) => setField('fc150_243', e.target.value)}
                  disabled={!isEditing}
                  maxLength={3}
                  style={{ width: 60 }}
                  placeholder="單位"
                />
              </div>
              <span className="form-label">輸單人員</span>
              <Input value={gf('fc150_27') as string} disabled />
              <span className="form-label">來樣顏色</span>
              <Input
                value={gf('fc150_29') as string}
                onChange={(e) => setField('fc150_29', e.target.value)}
                disabled={!isEditing}
                maxLength={20}
              />
              <span className="form-label">品牌名稱</span>
              <Input
                value={gf('fc150_30') as string}
                onChange={(e) => setField('fc150_30', e.target.value)}
                disabled={!isEditing}
                maxLength={30}
              />
              <span className="form-label">公司可提供品質</span>
              <Input
                value={gf('fc150_31') as string}
                onChange={(e) => setField('fc150_31', e.target.value)}
                disabled={!isEditing}
                maxLength={15}
              />
              <span className="form-label">狀態</span>
              <Input value={gf('fc150_80') as string} disabled />
              <span className="form-label">建立日期</span>
              <Input value={gf('fc150_33') ? String(gf('fc150_33')).substring(0, 10) : ''} disabled />
            </div>
            <div style={{ marginTop: 12 }}>
              <div className="form-label">布樣來源</div>
              <TextArea
                value={gf('fc150_21') as string}
                onChange={(e) => setField('fc150_21', e.target.value)}
                disabled={!isEditing}
                rows={2}
                maxLength={60}
              />
              <div className="form-label" style={{ marginTop: 8 }}>成本分析</div>
              <TextArea
                value={gf('fc150_23') as string}
                onChange={(e) => setField('fc150_23', e.target.value)}
                disabled={!isEditing}
                rows={2}
                maxLength={60}
              />
            </div>
          </div>
        </TabPane>

        {/* Tab 2: 產品組織規格 */}
        <TabPane tab="產品組織規格" key="2">
          <div className="form-section">
            <div className="form-grid">
              <span className="form-label">幅寬-針孔內</span>
              <InputNumber
                value={gf('fc150_17') as number}
                onChange={(v) => setField('fc150_17', v ?? 0)}
                disabled={!isEditing}
                style={{ width: '100%' }}
              />
              <span className="form-label">幅寬-針孔外</span>
              <InputNumber
                value={gf('fc150_18') as number}
                onChange={(v) => setField('fc150_18', v ?? 0)}
                disabled={!isEditing}
                style={{ width: '100%' }}
              />
              <span className="form-label">碼重-布重</span>
              <InputNumber
                value={gf('fc150_191') as number}
                onChange={(v) => setField('fc150_191', v ?? 0)}
                disabled={!isEditing}
                style={{ width: '100%' }}
              />
              <span className="form-label">米平方重-布重</span>
              <InputNumber
                value={gf('fc150_192') as number}
                onChange={(v) => setField('fc150_192', v ?? 0)}
                disabled={!isEditing}
                style={{ width: '100%' }}
              />
              <span className="form-label">碼重-成品重</span>
              <InputNumber
                value={gf('fc150_19') as number}
                onChange={(v) => setField('fc150_19', v ?? 0)}
                disabled={!isEditing}
                style={{ width: '100%' }}
              />
              <span className="form-label">膠重-碼重</span>
              <InputNumber
                value={gf('fc150_201') as number}
                onChange={(v) => setField('fc150_201', v ?? 0)}
                disabled={!isEditing}
                style={{ width: '100%' }}
              />
              <span className="form-label">膠重-米平方重</span>
              <InputNumber
                value={gf('fc150_202') as number}
                onChange={(v) => setField('fc150_202', v ?? 0)}
                disabled={!isEditing}
                style={{ width: '100%' }}
              />
              <span className="form-label">米平方重-成品重</span>
              <InputNumber
                value={gf('fc150_20') as number}
                onChange={(v) => setField('fc150_20', v ?? 0)}
                disabled={!isEditing}
                style={{ width: '100%' }}
              />
              <span className="form-label">厚度(MM)</span>
              <InputNumber
                value={gf('fc150_34') as number}
                onChange={(v) => setField('fc150_34', v ?? 0)}
                disabled={!isEditing}
                style={{ width: '100%' }}
              />
              <span className="form-label">來樣顏色</span>
              <Input
                value={gf('fc150_32') as string}
                onChange={(e) => setField('fc150_32', e.target.value)}
                disabled={!isEditing}
              />
            </div>
            <div style={{ marginTop: 12 }}>
              <div className="form-label">產品名稱</div>
              <TextArea
                value={gf('fc150_14') as string}
                onChange={(e) => setField('fc150_14', e.target.value)}
                disabled={!isEditing}
                rows={3}
              />
              <div className="form-label" style={{ marginTop: 8 }}>產品規格</div>
              <TextArea
                value={gf('fc150_15') as string}
                onChange={(e) => setField('fc150_15', e.target.value)}
                disabled={!isEditing}
                rows={3}
              />
              <div className="form-label" style={{ marginTop: 8 }}>加工別</div>
              <TextArea
                value={gf('fc150_16') as string}
                onChange={(e) => setField('fc150_16', e.target.value)}
                disabled={!isEditing}
                rows={3}
              />
            </div>
          </div>
        </TabPane>

        {/* Tab 3: 檢驗規範 */}
        <TabPane tab="檢驗規範" key="3">
          <div className="form-section">
            <div className="form-grid" style={{ marginBottom: 8 }}>
              <span className="form-label">總項數</span>
              <Input value={String(details.length)} disabled style={{ width: 60 }} />
            </div>
            {isEditing && (
              <div className="detail-actions">
                <Button type="primary" size="small" onClick={addDetailRow}>明細新增</Button>
              </div>
            )}
            <Table
              dataSource={details}
              columns={detailColumns}
              rowKey={(_, idx) => String(idx)}
              size="small"
              pagination={false}
              scroll={{ x: 600 }}
            />
          </div>
        </TabPane>

        {/* Tab 4: 備註 */}
        <TabPane tab="備註" key="4">
          <div className="form-section">
            <div className="form-grid">
              <span className="form-label">備註代號</span>
              <LookupField
                lookupType="remarkTemplate"
                value={gf('fc150_251') as string}
                onChange={(v) => setField('fc150_251', v)}
                onCodeConfirm={(v) => setField('fc150_251', v)}
                disabled={!isEditing}
                maxLength={2}
              />
              <span className="form-label" />
              <span />
            </div>
            <div style={{ marginTop: 8 }}>
              <div className="form-label">備註內容</div>
              <TextArea
                value={gf('fc150_252') as string}
                onChange={(e) => setField('fc150_252', e.target.value)}
                disabled={!isEditing}
                rows={4}
              />
            </div>
          </div>
        </TabPane>
      </Tabs>

      {/* ActionBar */}
      <div className="action-bar">
        <Button onClick={navFirst} disabled={currentIdx <= 0 || isEditing}>|◀</Button>
        <Button onClick={navPrev} disabled={currentIdx <= 0 || isEditing}>◀</Button>
        <Button onClick={navNext} disabled={currentIdx >= allIds.length - 1 || isEditing}>▶</Button>
        <Button onClick={navLast} disabled={currentIdx >= allIds.length - 1 || isEditing}>▶|</Button>
        <Button.Group>
          {!isEditing ? (
            <Button
              type="primary"
              onClick={handleAddNew}
              disabled={!permissions?.canCreate}
            >新增</Button>
          ) : (
            <Button
              type="primary"
              onClick={handleSaveClick}
              loading={loading}
            >存檔</Button>
          )}
          {!isEditing ? (
            <Button
              onClick={handleEdit}
              disabled={!record || !permissions?.canEdit}
            >修改</Button>
          ) : (
            <Button onClick={handleRevert}>還原</Button>
          )}
        </Button.Group>
        <Button
          danger
          onClick={handleDeleteClick}
          disabled={!record || isEditing || !permissions?.canDelete}
        >刪除</Button>
      </div>
    </div>
  );
};

export default C208Form;
